import React, { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { SegmentedControl } from '@components/primitives/SegmentedControl';
import {
  type ExpandableFieldItem,
  type SchedulingData,
  useAppointmentTypes,
} from '@features/appointment-types';
import {
  type Appointment as AppointmentResponse,
  AppointmentPropertiesTable,
  CustomRecurrenceModal,
  DirtyChangesAlert,
  type SeriesResponse,
  useAppointmentFormReset,
  useAppointmentFormValidation,
  useAppointmentSubmission,
  useDirtyStateTracking,
  useDurationSchedule,
  useFormsAndReminders,
  useOrgScheduleAccess,
  useOrgScheduleFilters,
  useRecurrence,
  useTelehealth,
} from '@features/appointments';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import { useOrganizationId, useTemplatesList } from '@features/user';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import {
  getModalityPlaceOfServiceError,
  getTelehealthForModality,
  normalizeCptCodes,
  type ConcreteVisitModality,
  type VisitModality,
} from '@utils/modalityPlaceOfService';

import { type Patient } from '../../../types/types';
import type { BlockedTimeOccurrence } from '../types/blockedTime';

import BlockedTimeForm, {
  type BlockedTimeActionState,
  type BlockedTimeFormHandle,
} from './BlockedTimeForm';

type CalendarCreateMode = 'appointment' | 'blocked_time';

const CREATE_MODE_OPTIONS = [
  { label: 'Appointment', value: 'appointment' },
  { label: 'Out of office', value: 'blocked_time' },
];

const isCalendarCreateMode = (value: string): value is CalendarCreateMode =>
  value === 'appointment' || value === 'blocked_time';

interface AddAppointmentModalProps {
  open: boolean;
  onClose: () => void;
  initialMode?: CalendarCreateMode;
  initialScheduledTime?: Date;
  initialDuration?: number;
  initialProviderId?: string;
  initialAllDay?: boolean;
  initialPatient?: Patient;
  blockedTime?: BlockedTimeOccurrence | null;
  existingAppointments?: AppointmentResponse[];
  onAppointmentCreated?: (appointment: AppointmentResponse | SeriesResponse) => void;
  onBlockedTimeSaved?: () => void;
  onBlockedTimeDeleted?: () => void;
}

const AddAppointmentModal: React.FC<AddAppointmentModalProps> = ({
  open,
  onClose,
  initialMode = 'appointment',
  initialScheduledTime,
  initialDuration,
  initialProviderId,
  initialAllDay = false,
  initialPatient,
  blockedTime,
  existingAppointments = [],
  onAppointmentCreated,
  onBlockedTimeSaved,
  onBlockedTimeDeleted,
}) => {
  const templatesList = useTemplatesList();
  const [enterKeyReleased, setEnterKeyReleased] = useState(true);
  const { appointment, blockedTime: blockedTimeTracking } = useEventTracking();
  const [activeMode, setActiveMode] = useState<CalendarCreateMode>(initialMode);
  const trackedModalOpenRef = useRef(false);
  const blockedTimeFormRef = useRef<BlockedTimeFormHandle>(null);
  const [blockedTimeActionState, setBlockedTimeActionState] = useState<BlockedTimeActionState>({
    isEditing: false,
    isSubmitting: false,
  });

  // Org schedule admin state
  const { isOrgViewActive } = useOrgScheduleFilters();
  const { currentUserId, hasOrgScheduleAccess } = useOrgScheduleAccess();
  const isOrgScheduleActive = hasOrgScheduleAccess && isOrgViewActive;
  const [selectedProviderId, setSelectedProviderId] = useState<string | undefined>(
    isOrgScheduleActive ? (initialProviderId ?? currentUserId) : undefined,
  );

  // Patient state
  const [selectedPatient, setSelectedPatient] = useState<Patient | undefined>(initialPatient);

  // Notes state
  const [notes, setNotes] = useState('');

  // Default labels (org-scoped) — pre-filled from the appointment type's defaults.
  const organizationId = useOrganizationId();
  const [labelIds, setLabelIds] = useState<string[]>([]);

  // Track source for analytics
  const [modalOpenSource, setModalOpenSource] = useState<string>('unknown');

  // Get appointment types for auto-selection (use selected provider in org mode)
  const { appointmentTypes } = useAppointmentTypes(
    undefined,
    isOrgScheduleActive ? selectedProviderId : undefined,
  );

  // Use modular hooks
  const durationSchedule = useDurationSchedule({
    initialDate: initialScheduledTime,
    initialDuration: initialDuration,
  });

  const recurrence = useRecurrence();

  const telehealth = useTelehealth();
  const [modality, setModality] = useState<VisitModality>(null);
  const [placeOfService, setPlaceOfService] = useState<string | null>(null);
  const [cptCodes, setCptCodes] = useState<string[]>([]);
  const [useJotmeet, setUseJotmeet] = useState(false);

  // Initialize dirty tracking first without dependencies
  const dirtyTrackingRef = useRef<any>(null);

  const formsAndReminders = useFormsAndReminders({
    // templateId: templatesList?.[0]?.template_id || null,
    onMarkDirty: field => dirtyTrackingRef.current?.markDirty(field),
  });

  const dirtyTracking = useDirtyStateTracking({
    selectedAppointmentType: formsAndReminders.selectedAppointmentType,
    selectedDuration: durationSchedule.selectedDuration,
    currentTelehealth: telehealth.telehealth,
    currentModality: modality,
    currentPlaceOfService: placeOfService,
    currentCptCodes: cptCodes,
    currentUseJotmeet: useJotmeet,
    currentForms: formsAndReminders.selectedForms,
    currentReminders: formsAndReminders.selectedReminders,
  });

  // Update ref
  dirtyTrackingRef.current = dirtyTracking;

  // Form validation
  const validation = useAppointmentFormValidation({
    selectedPatient,
    selectedAppointmentType: formsAndReminders.selectedAppointmentType,
    durationSchedule,
    mode: 'create',
  });

  // Form submission
  const submission = useAppointmentSubmission({
    mode: 'create',
    selectedPatient,
    selectedAppointmentType: formsAndReminders.selectedAppointmentType,
    durationSchedule,
    recurrence,
    templateId: templatesList?.[0]?.template_id || null,
    selectedForms: formsAndReminders.selectedForms,
    selectedReminders: formsAndReminders.selectedReminders,
    telehealth: telehealth.telehealth,
    modality,
    placeOfService,
    cptCodes,
    useJotmeet,
    notes: notes || undefined,
    hasDirtyChanges: dirtyTracking.showDirtyAlert,
    providerId: isOrgScheduleActive ? selectedProviderId : undefined,
    onAppointmentCreated,
    labelIds,
  });

  // Form reset
  const { resetForm: resetFormData } = useAppointmentFormReset({
    durationSchedule,
    recurrence,
    dirtyTracking,
    onPatientReset: () => setSelectedPatient(undefined),
  });

  // Coordinate between hooks - duration changes mark as dirty
  const handleDurationChange = (duration: number) => {
    durationSchedule.handleDurationSelect(duration);
    dirtyTracking.markDirty('duration');
  };

  // Coordinate between hooks - telehealth changes mark as dirty
  const handleTelehealthChange = (checked: boolean) => {
    telehealth.handleTelehealthChange(checked);
    setModality(checked ? 'video' : 'in_person');
    if (!checked) {
      setUseJotmeet(false);
    }
    dirtyTracking.markDirty('telehealth');
    dirtyTracking.markDirty('session');
  };

  const handleModalityChange = (nextModality: ConcreteVisitModality | null) => {
    setModality(nextModality);
    telehealth.setTelehealth(getTelehealthForModality(nextModality));
    if (nextModality !== 'video') {
      setUseJotmeet(false);
    }
    dirtyTracking.markDirty('session');
  };

  const handlePlaceOfServiceChange = (nextPlaceOfService: string | null) => {
    setPlaceOfService(nextPlaceOfService);
    dirtyTracking.markDirty('session');
  };

  const handleCptCodesChange = (nextCptCodes: string[]) => {
    setCptCodes(normalizeCptCodes(nextCptCodes));
    dirtyTracking.markDirty('session');
  };

  const handleUseJotmeetChange = (nextUseJotmeet: boolean) => {
    setUseJotmeet(nextUseJotmeet);
    dirtyTracking.markDirty('session');
  };

  // Set initial patient when it changes
  useEffect(() => {
    if (initialPatient) {
      setSelectedPatient(initialPatient);
    }
  }, [initialPatient]);

  useEffect(() => {
    if (open && isOrgScheduleActive) {
      setSelectedProviderId(initialProviderId ?? currentUserId);
    }
  }, [open, isOrgScheduleActive, initialProviderId, currentUserId]);

  useEffect(() => {
    if (open) {
      setActiveMode(blockedTime ? 'blocked_time' : initialMode);
    }
  }, [blockedTime, initialMode, open]);

  // Track modal opening
  useEffect(() => {
    if (!open) {
      trackedModalOpenRef.current = false;
      return;
    }
    if (trackedModalOpenRef.current) return;

    const source = initialPatient ? 'patient' : initialScheduledTime ? 'calendar' : 'quick_action';
    const openedMode = blockedTime ? 'blocked_time' : initialMode;
    setModalOpenSource(source);

    if (openedMode === 'blocked_time') {
      blockedTimeTracking.modalOpened({
        mode: blockedTime ? 'edit' : 'create',
        source: blockedTime ? 'event' : 'calendar',
        is_org_schedule: isOrgScheduleActive,
      });
    } else {
      appointment.modalOpened({ source, is_org_schedule: isOrgScheduleActive });
    }

    trackedModalOpenRef.current = true;
    // Snapshot modal-open metadata once per open; mode switches are tracked separately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Initialize date and time when modal opens
  useEffect(() => {
    if (open && !initialScheduledTime && !durationSchedule.date) {
      // Set smart defaults for date and time if not provided
      const now = new Date();

      // Calculate next 15-minute interval from now
      const currentMinutes = now.getMinutes();
      const roundedMinutes = Math.ceil(currentMinutes / 15) * 15;

      // Create the next appointment time
      const nextAppointmentTime = new Date(now);
      nextAppointmentTime.setMinutes(roundedMinutes, 0, 0); // Set seconds and milliseconds to 0

      // If rounding put us in the next hour, the Date object handles this automatically
      // If we're already at the end of the day, it will roll to the next day

      // Set date (could be today or tomorrow if we rolled over)
      const dateString = nextAppointmentTime.toISOString().split('T')[0];
      const timeString = nextAppointmentTime.toTimeString().substring(0, 5); // HH:mm format

      durationSchedule.setDate(dateString);
      durationSchedule.setStartTime(timeString);
    }
  }, [open, initialScheduledTime, durationSchedule]);

  // Reset all state when modal closes
  useEffect(() => {
    if (!open) {
      const timeoutId = setTimeout(() => {
        resetForm();
      }, 300);
      return () => clearTimeout(timeoutId);
    } else {
      // When modal opens, ensure patient is set from props
      if (initialPatient && !selectedPatient) {
        setSelectedPatient(initialPatient);
      }
    }
  }, [open, initialPatient]);

  // Auto-populate defaults from appointment type when not dirty
  useEffect(() => {
    if (!formsAndReminders.selectedAppointmentType) return;

    // Handle duration - check for conflict between initialDuration and appointment type default
    if (
      !dirtyTracking.isDirty('duration') &&
      formsAndReminders.selectedAppointmentType.default_duration
    ) {
      const appointmentTypeDefault = formsAndReminders.selectedAppointmentType.default_duration;
      const currentDuration = durationSchedule.selectedDuration;

      // If we have an initialDuration (from props) that differs from appointment type default
      if (initialDuration && initialDuration !== appointmentTypeDefault) {
        // Mark duration as dirty to show the conflict alert - this preserves the initialDuration
        dirtyTracking.markDirty('duration');
      } else if (currentDuration !== appointmentTypeDefault) {
        // No conflict or no initialDuration, apply the appointment type default if it's different
        durationSchedule.handleDurationSelect(appointmentTypeDefault);
      }
    }

    // Auto-populate forms if not dirty
    if (!dirtyTracking.isDirty('forms')) {
      formsAndReminders.populateFormsFromDefaults(formsAndReminders.selectedAppointmentType);
    }

    // Auto-populate reminders if not dirty
    if (!dirtyTracking.isDirty('reminders')) {
      formsAndReminders.populateRemindersFromDefaults(formsAndReminders.selectedAppointmentType);
    }

    // Auto-populate telehealth if not dirty
    if (
      !dirtyTracking.isDirty('telehealth') &&
      formsAndReminders.selectedAppointmentType.telehealth !== undefined
    ) {
      telehealth.setTelehealth(formsAndReminders.selectedAppointmentType.telehealth ?? false);
    }

    if (!dirtyTracking.isDirty('session')) {
      const defaultModality =
        formsAndReminders.selectedAppointmentType.default_modality ??
        (formsAndReminders.selectedAppointmentType.telehealth ? 'video' : null);
      setModality(defaultModality);
      telehealth.setTelehealth(getTelehealthForModality(defaultModality));
      setPlaceOfService(formsAndReminders.selectedAppointmentType.default_place_of_service ?? null);
      setCptCodes(normalizeCptCodes(formsAndReminders.selectedAppointmentType.default_cpt_codes));
      setUseJotmeet(
        Boolean(
          formsAndReminders.selectedAppointmentType.default_use_jotmeet &&
          defaultModality === 'video',
        ),
      );
    }
  }, [
    formsAndReminders.selectedAppointmentType,
    initialDuration,
    durationSchedule.selectedDuration,
  ]);

  // Pre-fill default labels when the selected appointment TYPE changes (keyed on
  // its id so editing other fields doesn't wipe manual label adjustments).
  const selectedTypeId = formsAndReminders.selectedAppointmentType?.encounter_type_id;
  useEffect(() => {
    setLabelIds(formsAndReminders.selectedAppointmentType?.default_label_ids ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTypeId]);

  // Auto-select first appointment type when types load, or re-select if current type is stale
  useEffect(() => {
    if (!appointmentTypes || appointmentTypes.length === 0) return;

    const currentType = formsAndReminders.selectedAppointmentType;

    // No type selected → auto-select first
    if (!currentType) {
      formsAndReminders.handleAppointmentTypeSelect(appointmentTypes[0]);
      return;
    }

    // Current type not in the new list (e.g. provider switched) → select first from new list
    const stillValid = appointmentTypes.some(
      t => t.encounter_type_id === currentType.encounter_type_id,
    );
    if (!stillValid) {
      formsAndReminders.handleAppointmentTypeSelect(appointmentTypes[0]);
    }
  }, [appointmentTypes, formsAndReminders.selectedAppointmentType]);

  // Handle creating a new encounter
  const handleCreateEncounter = async () => {
    const sessionError = getModalityPlaceOfServiceError({
      modality,
      placeOfService,
    });
    if (sessionError) {
      submission.setError(sessionError);
      return;
    }

    if (!validation.isFormValid) {
      return;
    }

    const startTime = Date.now();
    await submission.submit();

    // Reset form and close modal if successful
    if (!submission.error) {
      // Track successful creation
      appointment.create({
        appointment_type_id: formsAndReminders.selectedAppointmentType?.encounter_type_id,
        duration_minutes: durationSchedule.selectedDuration,
        is_recurring: recurrence.recurrence !== 'none',
        recurrence_pattern: recurrence.recurrence !== 'none' ? recurrence.recurrence : undefined,
        is_telehealth: telehealth.telehealth,
        has_forms: formsAndReminders.selectedForms && formsAndReminders.selectedForms.length > 0,
        forms_count: formsAndReminders.selectedForms?.length || 0,
        has_reminders:
          formsAndReminders.selectedReminders && formsAndReminders.selectedReminders.length > 0,
        reminders_count: formsAndReminders.selectedReminders?.length || 0,
        source: modalOpenSource,
        is_org_schedule: isOrgScheduleActive,
        target_provider_id: isOrgScheduleActive ? selectedProviderId : undefined,
      });

      // Track note addition if notes were included
      if (notes.trim()) {
        appointment.noteAdded({
          appointment_id: 'new',
          note_length: notes.trim().length,
          is_org_schedule: isOrgScheduleActive,
          target_provider_id: isOrgScheduleActive ? selectedProviderId : undefined,
        });
      }

      resetForm();
      onClose();
    } else {
      // Track creation failure
      appointment.creationFailed({
        error_type: 'submission_error',
        error_message: submission.error,
        is_org_schedule: isOrgScheduleActive,
      });
    }
  };

  // Reset the form and all modal state
  const resetForm = () => {
    resetFormData();
    setSelectedPatient(undefined);
    setNotes('');
    setLabelIds([]);
    setModality(null);
    setPlaceOfService(null);
    setCptCodes([]);
    setUseJotmeet(false);
    setSelectedProviderId(isOrgScheduleActive ? (initialProviderId ?? currentUserId) : undefined);
  };

  // Handle closing the modal
  const handleClose = () => {
    if (activeMode === 'appointment') {
      // Track abandonment if closing without creating
      appointment.creationAbandoned({
        source: modalOpenSource,
        had_patient_selected: !!selectedPatient,
        had_type_selected: !!formsAndReminders.selectedAppointmentType,
        is_org_schedule: isOrgScheduleActive,
      });
    }

    resetForm();
    onClose();
  };

  const handleModeChange = (nextMode: string) => {
    if (!isCalendarCreateMode(nextMode) || nextMode === activeMode) return;
    setActiveMode(nextMode);
    blockedTimeTracking.modeSelected({ mode: nextMode });
  };

  // Handle provider selection (org schedule admin)
  const handleProviderSelect = (providerId: string) => {
    setSelectedProviderId(providerId);
    // Reset appointment type since types are per-provider
    formsAndReminders.handleAppointmentTypeSelect(undefined);
  };

  // Handle patient selection
  const handlePatientSelect = (patient: Patient | undefined) => {
    setSelectedPatient(patient);
  };

  // Wrap appointment type selection to add tracking
  const handleAppointmentTypeSelect = (appointmentType: any) => {
    formsAndReminders.handleAppointmentTypeSelect(appointmentType);
    if (appointmentType) {
      appointment.typeSelected({
        appointment_type_id: appointmentType.encounter_type_id,
        color_enum: appointmentType.color,
      });
    }
  };

  // Wrap recurrence change to track when recurring is enabled
  const handleRecurrenceChange = (value: string) => {
    recurrence.handleRecurrenceChange(value);
    if (value !== 'none') {
      appointment.recurringEnabled({ recurrence_pattern: value });
    }
  };

  const toSchedulingData = (item: ExpandableFieldItem): SchedulingData => ({
    name: item.name,
    timing: item.timing ?? '',
    customTiming: item.offset ?? '',
    formId: item.formId,
  });

  const handleFormsChange = (nextForms: ExpandableFieldItem[]) => {
    for (const currentForm of formsAndReminders.selectedForms) {
      if (!nextForms.some(nextForm => nextForm.id === currentForm.id)) {
        formsAndReminders.handleRemoveForm(currentForm.id);
      }
    }

    for (const nextForm of nextForms) {
      const existingForm = formsAndReminders.selectedForms.find(form => form.id === nextForm.id);
      if (existingForm) {
        formsAndReminders.handleUpdateForm(nextForm.id, toSchedulingData(nextForm));
      } else {
        formsAndReminders.handleAddForm(toSchedulingData(nextForm));
      }
    }
  };

  const handleRemindersChange = (nextReminders: ExpandableFieldItem[]) => {
    for (const currentReminder of formsAndReminders.selectedReminders) {
      if (!nextReminders.some(nextReminder => nextReminder.id === currentReminder.id)) {
        formsAndReminders.handleRemoveReminder(currentReminder.id);
      }
    }

    for (const nextReminder of nextReminders) {
      const existingReminder = formsAndReminders.selectedReminders.find(
        reminder => reminder.id === nextReminder.id,
      );
      if (existingReminder) {
        formsAndReminders.handleUpdateReminder(nextReminder.id, toSchedulingData(nextReminder));
      } else {
        formsAndReminders.handleAddReminder(toSchedulingData(nextReminder));
      }
    }
  };

  // Handle dirty alert actions
  const handleUpdateAppointmentType = async () => {
    try {
      await formsAndReminders.handleUpdateAppointmentType(
        durationSchedule.selectedDuration,
        telehealth.telehealth,
        {
          modality,
          placeOfService,
          cptCodes,
          useJotmeet,
        },
      );
      dirtyTracking.resetDirtyState();
    } catch (error) {
      console.error('Failed to update appointment type:', error);
      submission.setError(
        error instanceof Error && error.message
          ? error.message
          : 'Failed to update appointment type. Please try again.',
      );
    }
  };

  // Handle Enter key press and release to submit form
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (activeMode !== 'appointment') return;

    if (event.key === 'Enter') {
      if (
        enterKeyReleased &&
        !event.shiftKey &&
        !event.ctrlKey &&
        validation.isFormValid &&
        !submission.isSubmitting
      ) {
        event.preventDefault();
        handleCreateEncounter();
      }
      setEnterKeyReleased(false);
    }
  };

  const handleKeyUp = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      setEnterKeyReleased(true);
    }
  };

  const sessionError = getModalityPlaceOfServiceError({
    modality,
    placeOfService,
  });

  // Don't render anything if modal is not open to prevent initialization issues
  if (!open) {
    return null;
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        scroll="paper"
        sx={{
          '& .MuiDialog-paper': {
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: ELIG_TOKENS.bg,
            color: ELIG_TOKENS.ink,
          },
        }}
        maxWidth={'md'}
      >
        <DialogTitle
          sx={{
            m: 0,
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            borderBottom: `1px solid ${ELIG_TOKENS.line}`,
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                color: ELIG_TOKENS.midnight,
                fontWeight: 650,
                lineHeight: 1.2,
              }}
            >
              {activeMode === 'blocked_time'
                ? blockedTime
                  ? 'Edit Out of Office'
                  : 'Out of Office'
                : 'New Appointment'}
            </Typography>
            <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
              {activeMode === 'blocked_time' ? 'Unavailable time' : 'Properties'}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" aria-label="Close appointment modal">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: activeMode === 'blocked_time' ? 0 : 3,
            pt: 0,
            pb: activeMode === 'blocked_time' ? 0 : 2.5,
          }}
        >
          <Stack
            spacing={activeMode === 'blocked_time' ? 0 : 2}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
          >
            {!blockedTime && (
              <Box
                sx={{
                  alignSelf: 'flex-start',
                  pt: 2,
                  ...(activeMode === 'blocked_time' ? { px: 3 } : {}),
                }}
              >
                <SegmentedControl
                  ariaLabel="Appointment creation mode"
                  options={CREATE_MODE_OPTIONS}
                  value={activeMode}
                  onChange={handleModeChange}
                  size="small"
                />
              </Box>
            )}

            {activeMode === 'blocked_time' ? (
              <BlockedTimeForm
                ref={blockedTimeFormRef}
                mode={blockedTime ? 'edit' : 'create'}
                blockedTime={blockedTime}
                initialScheduledTime={initialScheduledTime}
                initialDuration={initialDuration}
                initialProviderId={initialProviderId}
                initialAllDay={initialAllDay}
                isOrgScheduleActive={isOrgScheduleActive}
                currentUserId={currentUserId}
                appointments={existingAppointments}
                onClose={handleClose}
                onSaved={onBlockedTimeSaved}
                onDeleted={onBlockedTimeDeleted}
                hideActions
                onActionStateChange={setBlockedTimeActionState}
              />
            ) : (
              <>
                {submission.error && <Alert severity="error">{submission.error}</Alert>}
                <AppointmentPropertiesTable
                  mode="create"
                  showProvider={isOrgScheduleActive}
                  providerId={selectedProviderId}
                  onProviderSelect={handleProviderSelect}
                  patient={selectedPatient}
                  onPatientSelect={handlePatientSelect}
                  autoFocusPatientPicker={open && !initialPatient}
                  appointmentType={formsAndReminders.selectedAppointmentType}
                  onAppointmentTypeSelect={handleAppointmentTypeSelect}
                  date={durationSchedule.date}
                  startTime={durationSchedule.startTime}
                  duration={durationSchedule.selectedDuration}
                  onDateChange={durationSchedule.handleDateChange}
                  onStartTimeChange={durationSchedule.handleStartTimeChange}
                  onDurationChange={handleDurationChange}
                  recurrence={recurrence.recurrence}
                  onRecurrenceChange={handleRecurrenceChange}
                  onCustomRecurrenceClick={() => recurrence.setIsCustomRecurrenceModalOpen(true)}
                  telehealth={telehealth.telehealth}
                  onTelehealthChange={handleTelehealthChange}
                  modality={modality}
                  onModalityChange={handleModalityChange}
                  placeOfService={placeOfService}
                  onPlaceOfServiceChange={handlePlaceOfServiceChange}
                  cptCodes={cptCodes}
                  onCptCodesChange={handleCptCodesChange}
                  useJotmeet={useJotmeet}
                  onUseJotmeetChange={handleUseJotmeetChange}
                  sessionError={sessionError}
                  forms={formsAndReminders.selectedForms}
                  reminders={formsAndReminders.selectedReminders}
                  onFormsChange={handleFormsChange}
                  onRemindersChange={handleRemindersChange}
                  notes={notes}
                  onNotesChange={setNotes}
                  organizationId={organizationId}
                  labelIds={labelIds}
                  onLabelIdsChange={setLabelIds}
                />
              </>
            )}
          </Stack>
        </DialogContent>

        {activeMode === 'appointment' && (
          <DialogActions
            sx={{
              p: 2,
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 0,
              borderTop: `1px solid ${ELIG_TOKENS.line}`,
              bgcolor: 'background.paper',
              boxShadow: '0 -8px 24px rgba(28, 30, 133, 0.06)',
            }}
          >
            <DirtyChangesAlert
              show={dirtyTracking.showDirtyAlert}
              appointmentTypeName={formsAndReminders.selectedAppointmentType?.encounter_type || ''}
              changes={dirtyTracking.changes}
              selectedAppointmentType={formsAndReminders.selectedAppointmentType}
              currentDuration={durationSchedule.selectedDuration}
              currentTelehealth={telehealth.telehealth}
              currentModality={modality}
              currentPlaceOfService={placeOfService}
              currentCptCodes={cptCodes}
              currentUseJotmeet={useJotmeet}
              currentForms={formsAndReminders.selectedForms}
              currentReminders={formsAndReminders.selectedReminders}
              currentTemplate={
                templatesList?.find(
                  t => t.template_id === formsAndReminders.selectedAppointmentType?.template_id,
                ) || null
              }
              onUpdateAppointmentType={handleUpdateAppointmentType}
            />

            <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end' }}>
              <Button onClick={handleClose}>Cancel</Button>
              <Tooltip
                title={
                  !validation.isFormValid
                    ? validation.getValidationMessage()
                    : sessionError
                      ? sessionError
                      : validation.isFormValid && !submission.isSubmitting
                        ? 'Press Enter to create'
                        : ''
                }
                placement="top"
              >
                <span>
                  <Box sx={{ position: 'relative' }}>
                    <Button
                      variant="contained"
                      onClick={handleCreateEncounter}
                      disabled={
                        !validation.isFormValid || Boolean(sessionError) || submission.isSubmitting
                      }
                      sx={{
                        color: 'white',
                        paddingRight:
                          validation.isFormValid && !submission.isSubmitting ? '30px' : '16px',
                      }}
                    >
                      {submission.isSubmitting ? 'Saving...' : 'Save'}
                    </Button>
                    {validation.isFormValid && !submission.isSubmitting && (
                      <KeyboardReturnIcon
                        fontSize="small"
                        sx={{
                          position: 'absolute',
                          right: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          fontSize: '1rem',
                          opacity: 0.7,
                          color: 'white',
                        }}
                      />
                    )}
                  </Box>
                </span>
              </Tooltip>
            </Stack>
          </DialogActions>
        )}

        {activeMode === 'blocked_time' && (
          <DialogActions
            sx={{
              p: 2,
              alignItems: 'center',
              justifyContent: 'space-between',
              borderTop: `1px solid ${ELIG_TOKENS.line}`,
              bgcolor: 'background.paper',
              boxShadow: '0 -8px 24px rgba(28, 30, 133, 0.06)',
            }}
          >
            <Box>
              {blockedTimeActionState.isEditing && (
                <Button
                  color="error"
                  startIcon={<DeleteOutlineOutlinedIcon />}
                  onClick={() => blockedTimeFormRef.current?.remove()}
                  disabled={blockedTimeActionState.isSubmitting}
                >
                  Remove out of office
                </Button>
              )}
            </Box>
            <Stack direction="row" spacing={1.5}>
              <Button onClick={handleClose} disabled={blockedTimeActionState.isSubmitting}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => blockedTimeFormRef.current?.save()}
                disabled={blockedTimeActionState.isSubmitting}
              >
                {blockedTimeActionState.isSubmitting ? 'Saving...' : 'Save'}
              </Button>
            </Stack>
          </DialogActions>
        )}
      </Dialog>

      {/* Custom Recurrence Modal */}
      <CustomRecurrenceModal
        open={recurrence.isCustomRecurrenceModalOpen}
        onClose={() => recurrence.setIsCustomRecurrenceModalOpen(false)}
        onSave={recurrence.handleCustomRecurrenceSave}
        initialRule={recurrence.customRecurrenceRule || undefined}
      />
    </>
  );
};

export default AddAppointmentModal;
